import { FC, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import WeeklyGoalEphemeralMessageStroke from './WeeklyGoalEphemeralMessageStroke';
import { useWindowSize } from '../../custom-hooks/useWindowSize';

/**
 * Why this skill did not move the weekly goal. `alreadyInferred` covers the student who was
 * already credited for the skill by a diagnostic or a related assessment and has now passed its
 * challenge — they never "passed it before", so the `alreadyPassed` copy would be untrue.
 */
export type WeeklyGoalMissedReason = 'alreadyPassed' | 'alreadyInferred' | 'manyAttempts';

interface Props {
  reason: WeeklyGoalMissedReason;
}

const MESSAGES: Record<WeeklyGoalMissedReason, { heading: string; body: string }> = {
  alreadyPassed: {
    heading: 'You already passed this one!',
    body: 'Try a new skill to keep progressing toward your weekly goal.',
  },
  alreadyInferred: {
    heading: 'You already had this one!',
    body: 'Try a new skill to keep progressing toward your weekly goal.',
  },
  manyAttempts: {
    heading: 'Want to reach your weekly goal?',
    body: 'Try a new skill you haven’t passed yet.',
  },
};

const WeeklyGoalEphemeralMessage: FC<Props> = ({ reason }) => {
  const { width } = useWindowSize();
  const [position, setPosition] = useState<{ top: string | null; right: string | null }>({
    top: null,
    right: null,
  });
  const [visible, setVisible] = useState<boolean>(true);

  useEffect(() => {
    if (width < 992) setPosition({ top: '45px', right: '290px' });
    if (width >= 992) setPosition({ top: '45px', right: '400px' });
  }, [width]);

  return (
    visible && (
      <motion.div
        initial={{ opacity: 0, scale: 0, rotateY: -90 }}
        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
        transition={{
          duration: 1,
          delay: 1,
          ease: [0.34, 1.56, 0.64, 1],
        }}
        onAnimationComplete={() => {
          setTimeout(() => setVisible(false), 5000);
        }}
        style={{
          right: position.right ?? '',
          top: position.top ?? '',
        }}
        className={`tw-max-w-[335px] tw-border-solid tw-fixed tw-tw-flex tw-flex-col tw-px-4 tw-py-6 tw-bg-stone-0 tw-border-[4px] tw-rounded-3xl tw-border-neutral-100 tw-z-[4]`}
        // This is the only feedback telling the student their pass did not move the weekly goal,
        // and it self-dismisses, so it has to be announced rather than merely drawn.
        role="status"
      >
        <h3 className="tw-font-atkinson tw-non-italic tw-text-lg tw-text-neutral-950 tw-text-center tw-font-bold">
          {MESSAGES[reason].heading}
        </h3>
        <p className="tw-font-atkinson tw-non-italic tw-text-lg tw-font-normal tw-text-neutral-950 tw-text-center tw-m-0">
          {MESSAGES[reason].body}
        </p>
        <WeeklyGoalEphemeralMessageStroke />
      </motion.div>
    )
  );
};

export default WeeklyGoalEphemeralMessage;
